import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MessageIcon from '@mui/icons-material/Message';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import { useAuth } from '../../contexts/AuthContext';

// Hero section background gradient
const heroGradient = {
  background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(245,245,245,1) 100%)'
};

const features = [
  {
    title: 'AI-Powered Conversations',
    description: 'Chat with an intelligent agent that understands professional development frameworks and can provide personalized guidance.',
    icon: <MessageIcon fontSize="large" />
  },
  {
    title: 'Extensive Framework Library',
    description: 'Access a vast collection of structured, repeatable frameworks for building thriving user and developer ecosystems.',
    icon: <SchoolIcon fontSize="large" />
  },
  {
    title: 'Personalized Recommendations',
    description: 'Get tailored advice based on your role, goals, and specific challenges you\'re facing.',
    icon: <AutoGraphIcon fontSize="large" />
  },
  {
    title: 'Team Collaboration',
    description: 'Share frameworks and insights with your team to align and accelerate your professional development.',
    icon: <GroupIcon fontSize="large" />
  }
];

const frameworks = [
  'Audience Persona Development',
  'YouTube Channel Optimization',
  'Online Community Building',
  'Webinar Launch Strategy',
  'Social Media Content Planning'
];

const Landing = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ ...heroGradient, py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' }
                }}
              >
                Your AI-powered professional development coach
              </Typography>
              <Typography variant="h5" color="text.secondary" paragraph>
                Get personalized guidance on frameworks and strategies to help you grow your skills, team, and community.
              </Typography>
              <Box sx={{ mt: 4 }}>
                <Button 
                  component={RouterLink} 
                  to={isAuthenticated ? "/chat" : "/register"}
                  variant="contained" 
                  size="large"
                  sx={{ 
                    backgroundColor: 'black',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    mr: 2,
                    mb: { xs: 2, sm: 0 },
                    '&:hover': {
                      backgroundColor: '#333',
                    }
                  }}
                >
                  {isAuthenticated ? 'Start Chatting' : 'Sign Up Free'}
                </Button>
                <Button 
                  component={RouterLink} 
                  to="/about" 
                  variant="outlined" 
                  size="large"
                  sx={{ 
                    borderColor: 'black',
                    color: 'black',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#333',
                      backgroundColor: 'rgba(0,0,0,0.04)',
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                width: '100%', 
                height: '100%', 
                minHeight: '300px',
                backgroundColor: '#F0F0F0',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <Typography variant="h6" color="text.secondary">
                  Chat Interface Preview
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 600 }}>
          How StateShift Pathfinder helps you
        </Typography>
        <Typography variant="h6" color="text.secondary" textAlign="center" paragraph sx={{ mb: 8, maxWidth: '800px', mx: 'auto' }}>
          Get personalized guidance on frameworks and strategies tailored to your specific role and goals.
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ 
                height: '100%', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 2, color: 'black' }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* Frameworks Section */}
      <Box sx={{ backgroundColor: '#F9F9F9', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                Expert frameworks at your fingertips
              </Typography>
              <Typography variant="body1" paragraph>
                Access proven frameworks for professional development, team building, and community growth.
              </Typography>
              
              <List>
                {frameworks.map((framework, index) => (
                  <ListItem key={index} sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: '40px' }}>
                      <CheckCircleIcon sx={{ color: 'black' }} />
                    </ListItemIcon>
                    <ListItemText primary={framework} />
                  </ListItem>
                ))}
              </List>
              
              <Box sx={{ mt: 4 }}>
                <Button 
                  component={RouterLink} 
                  to={isAuthenticated ? "/chat" : "/register"}
                  variant="contained" 
                  size="large"
                  sx={{ 
                    backgroundColor: 'black',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#333',
                    }
                  }}
                >
                  Explore Frameworks
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                width: '100%', 
                height: '100%', 
                minHeight: '400px',
                backgroundColor: '#FFFFFF',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <Typography variant="h6" color="text.secondary">
                  Framework Visualization
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          Ready to accelerate your growth?
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
          Join thousands of professionals using StateShift Pathfinder to enhance their skills and build thriving communities.
        </Typography>
        <Button 
          component={RouterLink} 
          to={isAuthenticated ? "/chat" : "/register"}
          variant="contained" 
          size="large"
          sx={{ 
            backgroundColor: 'black',
            px: 6,
            py: 2,
            '&:hover': {
              backgroundColor: '#333',
            }
          }}
        >
          {isAuthenticated ? 'Start Chatting' : 'Get Started Free'}
        </Button>
      </Container>
    </Box>
  );
};

export default Landing;